import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ApiError,
  api,
  downloadPostFile,
  type Marketplace,
  type ReportExportBatch,
  type ReportExportBatchCreate,
  type ReportExportBatchStatus,
} from '../api/client';

const FORMAT_OPTIONS = ['PDF', 'XLSX', 'CSV'] as const;

const STATUS_PILL: Record<ReportExportBatchStatus, string> = {
  CREATED: 'pill-info',
  READY: 'pill-success',
  FAILED: 'pill-danger',
};

const STATUS_LABELS: Record<ReportExportBatchStatus, string> = {
  CREATED: 'Создана',
  READY: 'Готова',
  FAILED: 'Ошибка',
};

function currentMonthValue(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function monthToDate(month: string): string {
  return `${month}-01`;
}

function formatMonth(month: string): string {
  const d = new Date(month);
  return d.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('ru-RU', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

function formatBytes(value: number | null): string {
  if (!value) return '—';
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} КБ`;
  return `${(value / 1024 / 1024).toFixed(1)} МБ`;
}

export function ReportExportsPage() {
  const [month, setMonth] = useState(currentMonthValue());
  const [marketplace, setMarketplace] = useState<Marketplace>('WB');
  const [formats, setFormats] = useState<string[]>(['PDF', 'XLSX', 'CSV']);
  const [batches, setBatches] = useState<ReportExportBatch[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedMonthDate = useMemo(() => monthToDate(month), [month]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const params = new URLSearchParams({
        month: selectedMonthDate,
        marketplace,
        limit: '20',
      });
      const data = await api.get<ReportExportBatch[]>(
        `/api/report-export-batches?${params.toString()}`,
      );
      setBatches(data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setHistoryLoading(false);
    }
  }, [marketplace, selectedMonthDate]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  function toggleFormat(format: string) {
    setFormats((prev) => {
      if (prev.includes(format)) {
        return prev.length === 1 ? prev : prev.filter((item) => item !== format);
      }
      return [...prev, format];
    });
  }

  async function exportBatch() {
    const body: ReportExportBatchCreate = {
      marketplace,
      month: selectedMonthDate,
      formats,
    };
    setLoading(true);
    setError(null);
    try {
      await downloadPostFile('/api/report-export-batches', body);
      await loadHistory();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="section-header page-header">
        <div>
          <div className="rubric">Отчёты</div>
          <h1 className="text-h1 font-strong text-text-primary">Выгрузки</h1>
        </div>
      </div>

      <section className="panel p-4">
        <div className="grid gap-4 md:grid-cols-[160px_160px_1fr_auto] md:items-end">
          <label className="form-field">
            <span className="label">Месяц запуска</span>
            <input
              className="input"
              type="month"
              value={month}
              onChange={(event) => setMonth(event.target.value || currentMonthValue())}
            />
            <span className="text-caption text-text-tertiary">
              В ZIP попадут успешные отчёты, запущенные в выбранном месяце.
            </span>
          </label>

          <label className="form-field">
            <span className="label">Платформа</span>
            <select
              className="input"
              value={marketplace}
              onChange={(event) => setMarketplace(event.target.value as Marketplace)}
            >
              <option value="WB">WB</option>
            </select>
          </label>

          <div className="form-field">
            <span className="label">Форматы</span>
            <div className="flex flex-wrap gap-2">
              {FORMAT_OPTIONS.map((format) => {
                const selected = formats.includes(format);
                return (
                  <button
                    key={format}
                    type="button"
                    aria-pressed={selected}
                    className={[
                      'btn-sm rounded-md border transition',
                      selected
                        ? 'border-accent/45 bg-accent/15 text-accent'
                        : 'border-border-default/[0.14] bg-surface-2/50 text-text-secondary hover:bg-surface-2',
                    ].join(' ')}
                    onClick={() => toggleFormat(format)}
                  >
                    {format}
                  </button>
                );
              })}
            </div>
          </div>

          <button
            type="button"
            className="btn-primary btn-md"
            disabled={loading || formats.length === 0}
            onClick={() => void exportBatch()}
          >
            {loading ? 'Готовим…' : 'Скачать ZIP'}
          </button>
        </div>
        {error ? <div className="mt-3 text-label text-danger">{error}</div> : null}
      </section>

      <section className="panel overflow-hidden">
        <div className="section-header border-b border-border-default/[0.08] p-4">
          <div>
            <div className="rubric">История</div>
            <h2 className="text-h2 font-strong text-text-primary">
              Запуски за {formatMonth(selectedMonthDate)} · {marketplace}
            </h2>
          </div>
          <button
            type="button"
            className="btn-ghost btn-sm"
            disabled={historyLoading}
            onClick={() => void loadHistory()}
          >
            Обновить
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Создана</th>
                <th>Статус</th>
                <th>Форматы</th>
                <th>Прогоны</th>
                <th>Файл</th>
                <th>Ошибка</th>
              </tr>
            </thead>
            <tbody>
              {(batches ?? []).map((batch) => (
                <tr key={batch.id}>
                  <td>{formatDateTime(batch.created_at)}</td>
                  <td>
                    <span className={`pill ${STATUS_PILL[batch.status]}`}>
                      {STATUS_LABELS[batch.status]}
                    </span>
                  </td>
                  <td>{batch.formats.join(', ')}</td>
                  <td>{batch.report_run_ids.length}</td>
                  <td>
                    {batch.file_name ? (
                      <span title={batch.file_name}>
                        {batch.file_name} · {formatBytes(batch.file_size_bytes)}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{batch.error_message ?? '—'}</td>
                </tr>
              ))}
              {!historyLoading && (batches?.length ?? 0) === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-text-tertiary">
                    Выгрузок для запусков за выбранный месяц пока нет
                  </td>
                </tr>
              ) : null}
              {historyLoading ? (
                <tr>
                  <td colSpan={6} className="text-center text-text-tertiary">
                    Загрузка…
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
